import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import { BlogFooter, Cover, anchorOf, setMeta } from "../blog/BlogPages";
import { api } from "../rsvp/api";
import { fetchAllEvents } from "../pages/EventsPage";
import { whenText } from "../pages/PlanPage";
import { formatDayRange } from "../rsvp/format";
import cities, { findCity } from "./cities";
import "../blog/blog.css";
import "./cities.css";

/** /things-to-do: every city guide. */
export const CityListPage = () => {
  useEffect(() => {
    setMeta("Things to do · Blox", "Student guides to Lucknow, Delhi, Bangalore, Mumbai, Chennai, Vellore and Bhubaneswar: food, places and weekend plans.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="blog-page">
      <SiteHeader />
      <section className="blog-hero">
        <p className="blog-eyebrow">City guides</p>
        <h1>Things to do</h1>
        <p className="blog-lede">Where to eat, what to see and how to spend the weekend, written for students. Plus what's happening on Blox right now.</p>
      </section>
      <section className="blog-list">
        <div className="blog-grid">
          {cities.map((c, i) => (
            <Link to={`/things-to-do/${c.slug}`} className="blog-card" key={c.slug} style={{ animationDelay: `${i * 60}ms` }}>
              <Cover post={c} />
              <div className="blog-card-body">
                <p className="blog-meta"><span className="blog-chip">{c.state}</span></p>
                <h3>Things to do in {c.name}</h3>
                <p>{c.excerpt}</p>
                <span className="blog-read">Read the guide →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <BlogFooter />
    </main>
  );
};

/** Upcoming public Blox events in a city (matched on name, venue or address), with RSVP links. */
async function cityEvents(city) {
  const now = Date.now();
  const inCity = (e) => {
    const text = [e.event_name, e.event_venue, e.event_address].filter(Boolean).join(" ").toLowerCase();
    // Whole words only: "vit" must not match "invite".
    return city.match.some((m) => new RegExp(`\\b${m}\\b`).test(text));
  };
  const list = (await fetchAllEvents())
    .filter((e) => e.is_active && !e.private_event && !e.is_expired && new Date(e.event_end_date).getTime() > now && inCity(e))
    .sort((a, b) => new Date(a.event_start_date) - new Date(b.event_start_date))
    .slice(0, 4);
  const withLinks = await Promise.all(list.map(async (e) => {
    try { return { ...e, code: (await api.encrypt(String(e.id))).data?.encrypt_string }; }
    catch { return { ...e, code: null }; }
  }));
  return withLinks.filter((e) => e.code);
}

/** "Live on Blox" sidebar: plans in the city right now and upcoming events. */
const LiveInCity = ({ city }) => {
  const [plans, setPlans] = useState(null);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let gone = false;
    api.nearbyPlans(city.lat, city.lng)
      .then((res) => { if (!gone) setPlans((res.data?.plans ?? []).slice(0, 4)); })
      .catch(() => { if (!gone) setPlans([]); });
    cityEvents(city)
      .then((list) => { if (!gone) setEvents(list); })
      .catch(() => { if (!gone) setEvents([]); });
    return () => { gone = true; };
  }, [city]);

  return (
    <>
      <div className="blog-side-card">
        <p className="blog-side-title">Plans in {city.name} now</p>
        {plans === null ? (
          <p className="city-side-note">Loading…</p>
        ) : plans.length === 0 ? (
          <p className="city-side-note">No live plans yet. Start one in the Blox app and see who's in.</p>
        ) : (
          <ul className="blog-side-posts">
            {plans.map((p) => (
              <li key={p.id}>
                <Link to={`/plan/${p.id}`}>
                  <span className="blog-side-emoji" style={{ background: city.accent }} aria-hidden="true">{p.category?.emoji || "✨"}</span>
                  <span>I want to {p.title}<br /><small>{whenText(p)}</small></span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link to="/plans" className="blog-read">All plans near you →</Link>
      </div>

      <div className="blog-side-card">
        <p className="blog-side-title">Blox events in {city.name}</p>
        {events === null ? (
          <p className="city-side-note">Loading…</p>
        ) : events.length === 0 ? (
          <p className="city-side-note">Nothing scheduled here yet. Check back soon.</p>
        ) : (
          <ul className="blog-side-posts">
            {events.map((e) => (
              <li key={e.id}>
                <Link to={`/event/${e.code}`}>
                  <span className="blog-side-emoji" style={{ background: city.accent }} aria-hidden="true">🎟️</span>
                  <span>{e.event_name}<br /><small>{e.date_tbd ? "Date to be announced" : formatDayRange(e.event_start_date, e.event_end_date)}</small></span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link to="/events" className="blog-read">All events →</Link>
      </div>
    </>
  );
};

/** /things-to-do/:city: one city's guide. */
export const CityPage = () => {
  const { city: slug } = useParams();
  const city = findCity(slug);

  useEffect(() => {
    if (city) setMeta(`Things to do in ${city.name} · Blox`, city.excerpt);
    else setMeta("City not found · Blox", "We don't have a guide for this city yet.");
    window.scrollTo(0, 0);
  }, [city]);

  if (!city) {
    return (
      <main className="blog-page">
        <SiteHeader />
        <section className="blog-hero">
          <p className="blog-eyebrow">City guides</p>
          <h1 className="blog-title-small">No guide for this city yet</h1>
          <p className="blog-lede">Have a look at the cities we've written about so far.</p>
          <Link to="/things-to-do" className="blog-button">All city guides</Link>
        </section>
      </main>
    );
  }

  const sections = city.blocks.filter((b) => b.h).map((b) => b.h);
  const others = cities.filter((c) => c.slug !== city.slug).slice(0, 3);

  return (
    <main className="blog-page">
      <SiteHeader />
      <article className="blog-article">
        <header className="blog-article-head">
          <Link to="/things-to-do" className="blog-back">← All cities</Link>
          <p className="blog-meta"><span className="blog-chip">{city.state}</span><span>City guide</span></p>
          <h1 className="blog-article-title">Things to do in {city.name}</h1>
          <p className="blog-article-lede">{city.excerpt}</p>
        </header>
        <Cover post={city} large />

        <div className="blog-article-layout">
          <div className="blog-body">
            {city.blocks.map((b, i) => {
              if (b.h) return <h2 key={i} id={anchorOf(b.h)}>{b.h}</h2>;
              if (b.list) return <ul key={i}>{b.list.map((item) => <li key={item}>{item}</li>)}</ul>;
              if (b.quote) return <blockquote key={i}>{b.quote}</blockquote>;
              if (b.tip) return (
                <aside key={i} className="blog-tip">
                  {b.title && <strong>{b.title}</strong>}
                  <span>{b.tip}</span>
                </aside>
              );
              return <p key={i}>{b.p}</p>;
            })}
          </div>

          <aside className="blog-sidebar">
            <nav className="blog-side-card" aria-label="In this guide">
              <p className="blog-side-title">In this guide</p>
              <ol className="blog-toc">
                {sections.map((h) => <li key={h}><a href={`#${anchorOf(h)}`}>{h}</a></li>)}
              </ol>
            </nav>
            <LiveInCity city={city} />
          </aside>
        </div>
      </article>

      <section className="blog-related">
        <h2>More cities</h2>
        <div className="blog-grid">
          {others.map((c) => (
            <Link to={`/things-to-do/${c.slug}`} className="blog-card" key={c.slug}>
              <Cover post={c} />
              <div className="blog-card-body">
                <p className="blog-meta"><span className="blog-chip">{c.state}</span></p>
                <h3>Things to do in {c.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <BlogFooter />
    </main>
  );
};
